import React, { useCallback, useEffect, useRef, useState } from 'react';
import './PrerollAd.css';

const VAST_TIMEOUT_MS = 8000;
const MAX_WRAPPER_DEPTH = 3;
const QUARTILES = [['firstQuartile', 0.25], ['midpoint', 0.5], ['thirdQuartile', 0.75]];

const text = (node) => node?.textContent?.trim() || '';
const isHttpUrl = (value) => /^https?:\/\//i.test(value || '');

function parseTime(value) {
  const match = /^(\d+):(\d+):(\d+(?:\.\d+)?)$/.exec(value || '');
  return match ? Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]) : null;
}

function firePixels(urls) {
  (urls || []).filter(isHttpUrl).forEach((url) => { new Image().src = url; });
}

// Follows VAST wrappers and collects tracking from every level, as the spec requires.
async function loadVast(url, signal, depth = 0, acc = { impressions: [], tracking: {}, clickTracking: [] }) {
  const response = await fetch(url, { signal });
  const xml = new DOMParser().parseFromString(await response.text(), 'text/xml');
  const ad = xml.querySelector('Ad');
  if (!ad) throw new Error('VAST returned no ad');

  ad.querySelectorAll('Impression').forEach((n) => acc.impressions.push(text(n)));
  ad.querySelectorAll('Linear Tracking').forEach((n) => {
    const event = n.getAttribute('event');
    (acc.tracking[event] = acc.tracking[event] || []).push(text(n));
  });
  ad.querySelectorAll('ClickTracking').forEach((n) => acc.clickTracking.push(text(n)));

  const wrapperUrl = text(ad.querySelector('Wrapper VASTAdTagURI'));
  if (wrapperUrl) {
    if (depth >= MAX_WRAPPER_DEPTH || !isHttpUrl(wrapperUrl)) throw new Error('Invalid VAST wrapper');
    return loadVast(wrapperUrl, signal, depth + 1, acc);
  }

  const linear = ad.querySelector('InLine Linear');
  const media = Array.from(linear?.querySelectorAll('MediaFile') || [])
    .find((m) => m.getAttribute('type') === 'video/mp4' && isHttpUrl(text(m)));
  if (!media) throw new Error('VAST has no playable mp4');

  const clickThrough = text(linear.querySelector('ClickThrough'));
  return {
    ...acc,
    mediaUrl: text(media),
    clickThrough: isHttpUrl(clickThrough) ? clickThrough : null,
    skipOffset: parseTime(linear.getAttribute('skipoffset')),
  };
}

const ZONE_SKIP_AFTER_S = 5;
const ZONE_AUTO_CONTINUE_S = 15;

function PosterStart({ posterUrl, onStart }) {
  return (
    <button
      type="button"
      className="preroll-ad-start"
      style={posterUrl ? { backgroundImage: `url("${posterUrl}")` } : undefined}
      onClick={onStart}
      aria-label="Play video"
    >
      <span className="preroll-ad-play-icon" />
    </button>
  );
}

// Display-zone pre-roll (e.g. JuicyAds) shown over the player with a countdown.
function ZonePrerollAd({ zoneId, width, height, posterUrl, onComplete }) {
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const doneRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onComplete(true);
  }, [onComplete]);

  useEffect(() => {
    if (!started) return undefined;
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [started]);

  useEffect(() => {
    if (elapsed >= ZONE_AUTO_CONTINUE_S) finish();
  }, [elapsed, finish]);

  if (!started) {
    return (
      <div className="preroll-ad">
        <PosterStart posterUrl={posterUrl} onStart={() => setStarted(true)} />
      </div>
    );
  }

  const skipIn = Math.max(0, ZONE_SKIP_AFTER_S - elapsed);
  return (
    <div className="preroll-ad preroll-ad-zone">
      <iframe
        title="Advertisement"
        className="preroll-ad-zone-frame"
        src={`https://poweredby.jads.co/adshow.php?adzone=${encodeURIComponent(zoneId)}`}
        width={width}
        height={height}
        frameBorder="0"
        scrolling="no"
        allow="autoplay"
      />
      <div className="preroll-ad-label">Ad · Video starts in {ZONE_AUTO_CONTINUE_S - elapsed}s</div>
      <button type="button" className="preroll-ad-skip" disabled={skipIn > 0} onClick={finish}>
        {skipIn > 0 ? `Skip in ${skipIn}` : 'Skip Ad'}
      </button>
    </div>
  );
}

/**
 * Shows a pre-roll before the scene video: a VAST video ad, or a display zone when no VAST URL is set.
 * onComplete(autoPlay) is called once: autoPlay is true when the user already pressed play.
 */
function PrerollAd({ vastUrl, zoneId, width, height, posterUrl, onComplete }) {
  if (!vastUrl && zoneId) {
    return <ZonePrerollAd zoneId={zoneId} width={width} height={height} posterUrl={posterUrl} onComplete={onComplete} />;
  }
  return <VastPrerollAd vastUrl={vastUrl} posterUrl={posterUrl} onComplete={onComplete} />;
}

function VastPrerollAd({ vastUrl, posterUrl, onComplete }) {
  const [ad, setAd] = useState(null);
  const [started, setStarted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const firedRef = useRef(new Set());
  const doneRef = useRef(false);

  const finish = useCallback((autoPlay) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onComplete(autoPlay);
  }, [onComplete]);

  const track = useCallback((event, once = true) => {
    if (!ad || (once && firedRef.current.has(event))) return;
    firedRef.current.add(event);
    firePixels(ad.tracking[event]);
  }, [ad]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), VAST_TIMEOUT_MS);
    loadVast(vastUrl, controller.signal)
      .then(setAd)
      .catch((error) => {
        console.warn('Pre-roll ad unavailable', error);
        setAd(false);
      })
      .finally(() => clearTimeout(timer));
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [vastUrl]);

  useEffect(() => {
    if (ad === false) finish(started);
    if (!ad || !started) return;
    videoRef.current?.play().catch(() => finish(true));
  }, [ad, started, finish]);

  const handlePlaying = () => {
    if (firedRef.current.has('start')) return;
    firePixels(ad.impressions);
    track('creativeView');
    track('start');
  };

  const handleTimeUpdate = (e) => {
    const { currentTime: time, duration: total } = e.currentTarget;
    setCurrentTime(time);
    if (total > 0) {
      QUARTILES.forEach(([event, fraction]) => { if (time / total >= fraction) track(event); });
    }
  };

  const handleEnded = () => {
    track('complete');
    finish(true);
  };

  const handleSkip = () => {
    track('skip');
    finish(true);
  };

  const handleAdClick = () => {
    if (!ad.clickThrough) return;
    firePixels(ad.clickTracking);
    window.open(ad.clickThrough, '_blank', 'noopener,noreferrer');
  };

  const skipIn = ad?.skipOffset == null ? null : Math.max(0, Math.ceil(ad.skipOffset - currentTime));
  const remaining = duration > 0 ? Math.max(0, Math.ceil(duration - currentTime)) : null;

  return (
    <div className="preroll-ad">
      {ad ? (
        <video
          ref={videoRef}
          className="preroll-ad-video"
          src={ad.mediaUrl}
          playsInline
          preload="auto"
          style={{ visibility: started ? 'visible' : 'hidden', cursor: ad.clickThrough ? 'pointer' : 'default' }}
          onClick={handleAdClick}
          onPlaying={handlePlaying}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onError={() => finish(true)}
        />
      ) : null}

      {!started ? (
        <PosterStart posterUrl={posterUrl} onStart={() => setStarted(true)} />
      ) : (
        <>
          <div className="preroll-ad-label">Ad{remaining != null ? ` · ${remaining}s` : ''}</div>
          {skipIn != null ? (
            <button type="button" className="preroll-ad-skip" disabled={skipIn > 0} onClick={handleSkip}>
              {skipIn > 0 ? `Skip in ${skipIn}` : 'Skip Ad'}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

export default PrerollAd;
